import React from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ru as dateFnsRu } from 'date-fns/locale/ru';
import { enGB as dateFnsEn } from 'date-fns/locale/en-GB';

import { TableSizeOptions } from '../../components/TableSizeFilter/constants';
import { LimitOptions } from '../../components/LimitFilter/constants';
import { DateOptions } from '../../components/DateFilter/constants';
import { PokersiteOptions } from '../../components/PokersiteFilter/constants';
import type { CustomDateFilterValue } from '../../components/DateFilter/DateFilter';
import { getDateLabel, getLimitLabel, getPokersiteLabel } from '../utils/filterLabels';
import useOutsideClick from '../../hooks/useOutsideClick';

import './V2FilterBar.css';

registerLocale('ru', dateFnsRu);
registerLocale('en', dateFnsEn);

const isValidDate = (date: Date | null | undefined) =>
  date instanceof Date && !Number.isNaN(date.getTime());

const getDateFormat = (lang: string) => (lang.startsWith('ru') ? 'dd.MM.yyyy' : 'dd/MM/yyyy');

const DATE_PRESET_LABELS: Record<number, string> = {
  1: 'Этот месяц',
  2: '3 месяца',
  3: '6 месяцев',
  4: '12 месяцев',
};

type PillProps = {
  label: string;
  pressed: boolean;
  onClick: () => void;
};

const FilterPill: React.FC<PillProps> = ({ label, pressed, onClick }) => (
  <button type="button" className="v2-filter-pill" aria-pressed={pressed} onClick={onClick}>
    {label}
  </button>
);

export type V2FilterBarValues = {
  tableSizeId?: number;
  limitId: number;
  dateId?: number;
  customDate?: CustomDateFilterValue;
  pokersiteId?: number;
};

type V2FilterBarProps = V2FilterBarValues & {
  onTableSizeChange: (value?: number) => void;
  onLimitChange: (value: number) => void;
  onDateChange: (value?: number, customValue?: CustomDateFilterValue) => void;
  onPokersiteChange?: (value: number) => void;
};

const V2FilterBar: React.FC<V2FilterBarProps> = ({
  tableSizeId,
  limitId,
  dateId,
  customDate,
  pokersiteId = 0,
  onTableSizeChange,
  onLimitChange,
  onDateChange,
  onPokersiteChange,
}) => {
  const { t, i18n } = useTranslation('translation');

  const siteDropdownRef = React.useRef<HTMLDivElement>(null);
  const limitDropdownRef = React.useRef<HTMLDivElement>(null);
  const periodDropdownRef = React.useRef<HTMLDivElement>(null);
  const [siteOpen, setSiteOpen] = React.useState(false);
  const [limitOpen, setLimitOpen] = React.useState(false);
  const [periodOpen, setPeriodOpen] = React.useState(false);

  const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const dateFormat = getDateFormat(i18n.language);
  const dateLocale = i18n.language.startsWith('ru') ? 'ru' : 'en';

  useOutsideClick(siteDropdownRef, () => setSiteOpen(false));
  useOutsideClick(limitDropdownRef, () => setLimitOpen(false));
  useOutsideClick(periodDropdownRef, () => setPeriodOpen(false));

  React.useEffect(() => {
    if (!customDate) {
      setDateRange([null, null]);
      return;
    }

    setDateRange([new Date(customDate.fromDate), new Date(customDate.toDate)]);
  }, [customDate]);

  const commitCustomDateRange = React.useCallback(() => {
    if (!isValidDate(startDate) && !isValidDate(endDate)) {
      return;
    }

    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      if (customDate) {
        setDateRange([new Date(customDate.fromDate), new Date(customDate.toDate)]);
      } else {
        setDateRange([null, null]);
      }
      return;
    }

    const from = dayjs(startDate);
    const to = dayjs(endDate);
    const normalizedFrom = from.isAfter(to) ? to : from;
    const normalizedTo = from.isAfter(to) ? from : to;

    onDateChange(undefined, {
      fromDate: normalizedFrom.format('YYYY-MM-DD'),
      toDate: normalizedTo.format('YYYY-MM-DD'),
    });
    setPeriodOpen(false);
  }, [customDate, endDate, onDateChange, startDate]);

  const handleDateInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitCustomDateRange();
      }
    },
    [commitCustomDateRange],
  );

  const handlePresetDate = React.useCallback(
    (value?: number) => {
      onDateChange(value, undefined);
      setPeriodOpen(false);
    },
    [onDateChange],
  );

  return (
    <div className="v2-global-filters" aria-label="Фильтры">
      {onPokersiteChange ? (
        <div className="v2-filter-group v2-filter-group--menu" ref={siteDropdownRef}>
          <p className="v2-filter-group__label">Сеть</p>
          <button
            type="button"
            className="v2-filter-trigger"
            aria-haspopup="listbox"
            aria-expanded={siteOpen}
            onClick={() => setSiteOpen((open) => !open)}
          >
            <span>{getPokersiteLabel(pokersiteId)}</span>
            <span className="v2-filter-trigger__chevron" aria-hidden="true">
              ↓
            </span>
          </button>
          {siteOpen ? (
            <div className="v2-filter-menu" role="listbox" aria-label="Сеть">
              {PokersiteOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="v2-filter-menu__item"
                  role="option"
                  aria-selected={pokersiteId === option.id}
                  onClick={() => {
                    onPokersiteChange(option.id);
                    setSiteOpen(false);
                  }}
                >
                  {getPokersiteLabel(option.id)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="v2-filter-group">
        <p className="v2-filter-group__label">Формат</p>
        <div className="v2-filter-pills" role="group" aria-label="Формат стола">
          <FilterPill
            label="Все"
            pressed={tableSizeId == null}
            onClick={() => onTableSizeChange(undefined)}
          />
          {TableSizeOptions.map((option) => (
            <FilterPill
              key={option.id}
              label={option.label}
              pressed={tableSizeId === option.id}
              onClick={() => onTableSizeChange(option.id)}
            />
          ))}
        </div>
      </div>

      <div className="v2-filter-group v2-filter-group--menu" ref={limitDropdownRef}>
        <p className="v2-filter-group__label">Лимит</p>
        <button
          type="button"
          className="v2-filter-trigger"
          aria-haspopup="listbox"
          aria-expanded={limitOpen}
          onClick={() => setLimitOpen((open) => !open)}
        >
          <span>{getLimitLabel(limitId)}</span>
          <span className="v2-filter-trigger__chevron" aria-hidden="true">
            ↓
          </span>
        </button>
        {limitOpen ? (
          <div className="v2-filter-menu" role="listbox" aria-label="Лимит">
            {LimitOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className="v2-filter-menu__item"
                role="option"
                aria-selected={limitId === option.id}
                onClick={() => {
                  onLimitChange(option.id);
                  setLimitOpen(false);
                }}
              >
                {getLimitLabel(option.id)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="v2-filter-group v2-filter-group--menu" ref={periodDropdownRef}>
        <p className="v2-filter-group__label">Период</p>
        <button
          type="button"
          className="v2-filter-trigger"
          aria-haspopup="dialog"
          aria-expanded={periodOpen}
          onClick={() => setPeriodOpen((open) => !open)}
        >
          <span>{getDateLabel(dateId, customDate)}</span>
          <span className="v2-filter-trigger__chevron" aria-hidden="true">
            ↓
          </span>
        </button>
        {periodOpen ? (
          <div className="v2-filter-menu v2-filter-menu--period" role="dialog" aria-label="Период">
            <div className="v2-filter-menu__section">
              <button
                type="button"
                className="v2-filter-menu__item"
                aria-current={dateId == null && !customDate ? 'true' : undefined}
                onClick={() => handlePresetDate(undefined)}
              >
                Всё время
              </button>
              {DateOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="v2-filter-menu__item"
                  aria-current={dateId === option.id ? 'true' : undefined}
                  onClick={() => handlePresetDate(option.id)}
                >
                  {DATE_PRESET_LABELS[option.id] ?? option.label}
                </button>
              ))}
            </div>
            <div className="v2-filter-menu__divider" />
            <div className="v2-filter-menu__section">
              <p className="v2-filter-menu__caption">Точные даты</p>
              <div className="v2-filter-menu__picker">
                <DatePicker
                  locale={dateLocale}
                  placeholderText={t('select_date_range')}
                  showPopperArrow={false}
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat={dateFormat}
                  isClearable
                  allowSameDay
                  shouldCloseOnSelect={false}
                  onChange={(value) => setDateRange(value ?? [null, null])}
                  onCalendarClose={commitCustomDateRange}
                  onBlur={commitCustomDateRange}
                  onKeyDown={handleDateInputKeyDown}
                  excludeDateIntervals={[
                    {
                      start: new Date(),
                      end: new Date('2030-01-01'),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default V2FilterBar;
